import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import profesorParticularApiService from '../services/ProfesorParticularApiService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import UserSessionMenu from './common/UserSessionMenu';
import SearchableSelect from './common/SearchableSelect';
import { useAuth } from '../contexts/AuthContext';
import { isAdminColegio } from '../utils/adminMenu';

// Administradores de una cuenta de particular: el dueño (siempre administrador) y los profesores
// de la cuenta marcados como administradores. Editar, activar y resetear contraseña reutilizan los
// endpoints de profesores asociados.

const emptyForm = {
  email: '',
  dni: '',
  nombre: '',
  apellido: '',
  telefono: ''
};

const getValue = (source, keys, fallback = '') => {
  if (!source) return fallback;
  const value = keys.map((key) => source[key]).find((item) => item !== undefined && item !== null);
  return value ?? fallback;
};

const normalizeUsuario = (item) => ({
  id: String(getValue(item, ['id', 'Id'])),
  email: getValue(item, ['email', 'Email']),
  dni: getValue(item, ['dni', 'Dni']),
  nombre: getValue(item, ['nombre', 'Nombre']),
  apellido: getValue(item, ['apellido', 'Apellido']),
  displayName: getValue(item, ['displayName', 'DisplayName']),
  telefono: getValue(item, ['telefono', 'Telefono']),
  activo: Boolean(getValue(item, ['activo', 'Activo'], true)),
  esAdministrador: Boolean(getValue(item, ['esAdministrador', 'EsAdministrador'], false)),
  esDueno: Boolean(getValue(item, ['esDueno', 'EsDueno'], false))
});

const getNombre = (usuario) => {
  if (!usuario) return 'el usuario';
  return `${usuario.nombre || ''} ${usuario.apellido || ''}`.trim() || usuario.displayName || usuario.email || 'el usuario';
};

const getResetPassword = (usuario) => String(usuario?.dni || '').trim();

const validateForm = (values) => {
  if (!values.email.trim()) return 'Completa el email para continuar.';
  if (!values.dni.trim()) return 'Completa el DNI para continuar.';
  if (values.dni.trim().length < 6) return 'El DNI debe tener al menos 6 caracteres.';
  return '';
};

const AdminProfesorParticularAdministradores = () => {
  const navigate = useNavigate();
  const { particularId } = useParams();
  const { user, logout } = useAuth();
  const isGeneralAdmin = !isAdminColegio(user);

  const [particular, setParticular] = useState(null);
  const [administradores, setAdministradores] = useState([]);
  const [profesores, setProfesores] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [mode, setMode] = useState('nuevo');
  const [form, setForm] = useState(emptyForm);
  const [selectedProfesorId, setSelectedProfesorId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [editModal, setEditModal] = useState({ open: false, usuario: null });
  const [editForm, setEditForm] = useState(emptyForm);
  const [editError, setEditError] = useState('');
  const [toggleModal, setToggleModal] = useState({ open: false, usuario: null, nextActivo: true });
  const [quitarModal, setQuitarModal] = useState({ open: false, usuario: null });
  const [resetModal, setResetModal] = useState({ open: false, usuario: null });
  const [resultModal, setResultModal] = useState({ open: false, ok: true, title: '', message: '' });

  const particularNombre = getValue(particular, ['nombreVisible', 'NombreVisible', 'nombre', 'Nombre'], 'Particular');
  const tieneInstitucion = Boolean(getValue(particular, ['institucionNombre', 'InstitucionNombre']));

  useEffect(() => {
    if (!isGeneralAdmin) {
      navigate('/admin/select-colegio', { replace: true });
    }
  }, [isGeneralAdmin, navigate]);

  const loadData = useCallback(async () => {
    if (!particularId) return;
    setListLoading(true);
    setListError('');
    try {
      const [adminsRes, profesoresList] = await Promise.all([
        profesorParticularApiService.getAdministradores(particularId),
        profesorParticularApiService.getProfesores(particularId)
      ]);
      setParticular(adminsRes.particular);
      setAdministradores(adminsRes.administradores.map(normalizeUsuario));
      setProfesores(profesoresList.map(normalizeUsuario));
    } catch (error) {
      setListError(error.message || 'No pudimos cargar los administradores.');
    } finally {
      setListLoading(false);
    }
  }, [particularId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Profesores activos de la cuenta que todavía no son administradores.
  const profesorOptions = useMemo(() => (
    profesores
      .filter((profesor) => profesor.activo && !profesor.esAdministrador)
      .map((profesor) => ({
        value: profesor.id,
        label: getNombre(profesor),
        secondary: profesor.email,
        searchText: `${profesor.nombre} ${profesor.apellido} ${profesor.email} ${profesor.dni}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [profesores]);

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const validation = validateForm(form);
    if (validation) {
      showMessage(validation, 'error');
      return;
    }

    setIsSubmitting(true);
    showMessage('Creando administrador...', 'info');
    try {
      await profesorParticularApiService.createAdministrador(particularId, form);
      setForm(emptyForm);
      await loadData();
      showMessage('Administrador creado correctamente. Su contraseña inicial es su DNI.', 'success');
    } catch (error) {
      showMessage(error.message || 'No pudimos crear el administrador.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePromote = async () => {
    if (!selectedProfesorId) return;
    const profesor = profesores.find((item) => item.id === String(selectedProfesorId));

    setIsSubmitting(true);
    showMessage('Asignando rol de administrador...', 'info');
    try {
      await profesorParticularApiService.setProfesorAdministrador(particularId, selectedProfesorId, true);
      setSelectedProfesorId('');
      await loadData();
      showMessage(`${getNombre(profesor)} ahora es administrador de la cuenta.`, 'success');
    } catch (error) {
      showMessage(error.message || 'No pudimos asignar el rol de administrador.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (usuario) => {
    setEditForm({
      email: usuario.email || '',
      dni: usuario.dni || '',
      nombre: usuario.nombre || '',
      apellido: usuario.apellido || '',
      telefono: usuario.telefono || ''
    });
    setEditError('');
    setEditModal({ open: true, usuario });
  };

  const handleSaveEdit = async () => {
    if (!editModal.usuario) return;
    const validation = validateForm(editForm);
    if (validation) {
      setEditError(validation);
      return;
    }

    setActionLoading(true);
    setEditError('');
    try {
      await profesorParticularApiService.updateProfesor(particularId, editModal.usuario.id, editForm);
      await loadData();
      setEditModal({ open: false, usuario: null });
      showMessage('Administrador actualizado correctamente.', 'success');
    } catch (error) {
      setEditError(error.message || 'No pudimos actualizar el administrador.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActivo = async () => {
    if (!toggleModal.usuario) return;
    setActionLoading(true);
    try {
      await profesorParticularApiService.updateProfesorActivo(particularId, toggleModal.usuario.id, toggleModal.nextActivo);
      await loadData();
      showMessage(
        toggleModal.nextActivo
          ? `${getNombre(toggleModal.usuario)} fue activado.`
          : `${getNombre(toggleModal.usuario)} fue desactivado.`,
        'success'
      );
    } catch (error) {
      showMessage(error.message || 'No pudimos actualizar el estado.', 'error');
    } finally {
      setActionLoading(false);
      setToggleModal({ open: false, usuario: null, nextActivo: true });
    }
  };

  const handleQuitarRol = async () => {
    if (!quitarModal.usuario) return;
    setActionLoading(true);
    try {
      await profesorParticularApiService.setProfesorAdministrador(particularId, quitarModal.usuario.id, false);
      await loadData();
      showMessage(`${getNombre(quitarModal.usuario)} ya no es administrador. Sigue como profesor de la cuenta.`, 'success');
    } catch (error) {
      showMessage(error.message || 'No pudimos quitar el rol de administrador.', 'error');
    } finally {
      setActionLoading(false);
      setQuitarModal({ open: false, usuario: null });
    }
  };

  const handleResetPassword = async () => {
    if (!resetModal.usuario) return;
    const newPassword = getResetPassword(resetModal.usuario);

    setActionLoading(true);
    try {
      await profesorParticularApiService.resetProfesorPassword(particularId, resetModal.usuario.id);
      setResultModal({ open: true, ok: true, title: 'Contraseña reseteada', message: `Nuevo password: ${newPassword}.` });
    } catch (error) {
      setResultModal({ open: true, ok: false, title: 'No se pudo resetear', message: error.message || 'No pudimos resetear la contraseña.' });
    } finally {
      setActionLoading(false);
      setResetModal({ open: false, usuario: null });
    }
  };

  return (
    <div className="admin-standalone">
      <header className="admin-standalone-header">
        <div>
          <p className="eyebrow">Administradores de la cuenta</p>
          <h1>{particularNombre}</h1>
          <p className="subtitle">
            {tieneInstitucion ? 'Institución' : 'Particular'} · Dueño: {getValue(particular, ['nombre', 'Nombre'], '-')}
          </p>
        </div>
        <div className="admin-standalone-actions">
          <ActiveColegioBadge />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/select-colegio', { state: { scope: 'particulares' } })}
          >
            <i className="fas fa-arrow-left" />
            <span>Volver a particulares</span>
          </button>
          <UserSessionMenu user={user} onLogout={logout} showStandaloneLogout={false} userFallbackLabel="Administrador" />
        </div>
      </header>

      <div className="admin-dual-panel">
        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-user-shield" />
              <span>Agregar administrador</span>
            </div>
          </div>
          <div className="card-content">
            <p style={{ marginTop: 0, color: '#475569' }}>
              Los administradores gestionan la cuenta igual que el dueño: cursos, profesores, invitaciones y cuenta corriente. También pueden dictar cursos.
            </p>

            <div className="particular-admin-mode-toggle" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'nuevo'}
                className={mode === 'nuevo' ? 'active' : ''}
                onClick={() => setMode('nuevo')}
              >
                <i className="fas fa-user-plus" />
                <span>Nuevo usuario</span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'profesor'}
                className={mode === 'profesor' ? 'active' : ''}
                onClick={() => setMode('profesor')}
              >
                <i className="fas fa-chalkboard-teacher" />
                <span>Profesor de la cuenta</span>
              </button>
            </div>

            {mode === 'nuevo' && (
              <form onSubmit={handleCreate} className="prof-create-form">
                <p className="particular-form-section-help" style={{ marginTop: '0.75rem' }}>
                  <strong>La contraseña inicial será el DNI</strong>; el usuario podrá cambiarla luego.
                </p>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Email *</label>
                    <input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} placeholder="admin@ejemplo.com" />
                  </div>
                  <div className="form-field">
                    <label>DNI *</label>
                    <input type="text" value={form.dni} onChange={(e) => setForm((prev) => ({ ...prev, dni: e.target.value }))} placeholder="12345678" />
                  </div>
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Nombre</label>
                    <input type="text" value={form.nombre} onChange={(e) => setForm((prev) => ({ ...prev, nombre: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>Apellido</label>
                    <input type="text" value={form.apellido} onChange={(e) => setForm((prev) => ({ ...prev, apellido: e.target.value }))} />
                  </div>
                </div>
                <div className="form-field">
                  <label>Teléfono</label>
                  <input type="text" value={form.telefono} onChange={(e) => setForm((prev) => ({ ...prev, telefono: e.target.value }))} />
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                    <span>Crear administrador</span>
                  </button>
                </div>
              </form>
            )}

            {mode === 'profesor' && (
              <div className="form-field" style={{ marginTop: '1rem' }}>
                <label>Profesor</label>
                <SearchableSelect
                  value={selectedProfesorId}
                  onChange={setSelectedProfesorId}
                  options={profesorOptions}
                  placeholder="Busca por nombre, email o DNI"
                  emptyText="No hay profesores activos de la cuenta que no sean administradores."
                  noResultsText="No encontramos profesores con esa búsqueda."
                  disabled={isSubmitting}
                />
                <div className="form-actions">
                  <button type="button" className="btn btn-primary" onClick={handlePromote} disabled={isSubmitting || !selectedProfesorId}>
                    {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-user-shield" />}
                    <span>Hacer administrador</span>
                  </button>
                </div>
              </div>
            )}

            {message && (
              <div
                className="modal-message"
                style={{
                  marginTop: '0.5rem',
                  borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                  background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                  color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                }}
              >
                {message}
              </div>
            )}
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-users-cog" />
              <span>Administradores ({administradores.length})</span>
            </div>
            <div className="professor-actions">
              <button className="refresh-btn" type="button" onClick={loadData} disabled={listLoading} title="Recargar">
                <i className={`fas ${listLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
              </button>
            </div>
          </div>
          <div className="card-content professors-table">
            {listLoading && administradores.length === 0 && (
              <div className="data-table-loading">
                <div className="loading-spinner"><i className="fas fa-spinner fa-spin" /></div>
                <p>Cargando administradores...</p>
              </div>
            )}

            {!listLoading && listError && (
              <div className="data-table-empty">
                <div className="empty-icon"><i className="fas fa-exclamation-triangle" /></div>
                <p>{listError}</p>
                <button className="refresh-btn" type="button" onClick={loadData}>
                  <i className="fas fa-sync-alt" /> Reintentar
                </button>
              </div>
            )}

            {!listError && administradores.length > 0 && (
              <div className="data-table-wrapper">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nombre</th>
                      <th>Email</th>
                      <th className="text-center width-sm">Estado</th>
                      <th className="text-center width-sm">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {administradores.map((usuario) => (
                      <tr key={usuario.id}>
                        <td>
                          {getNombre(usuario)}
                          <span className={`status-chip ${usuario.esDueno ? 'warning' : 'info'}`} style={{ marginLeft: '0.5rem' }}>
                            {usuario.esDueno ? 'Dueño' : 'Administrador'}
                          </span>
                        </td>
                        <td>{usuario.email || '-'}</td>
                        <td className="text-center">
                          <span className={`status-chip ${usuario.activo ? 'success' : 'danger'}`}>
                            {usuario.activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          {usuario.esDueno ? (
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="action-edit-btn"
                                onClick={() => navigate(`/admin/particulares/${particularId}/editar`)}
                                title="Editar datos de la cuenta y del dueño"
                              >
                                <i className="fas fa-edit" />
                              </button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <button type="button" className="action-edit-btn" onClick={() => openEditModal(usuario)} title="Editar administrador">
                                <i className="fas fa-edit" />
                              </button>
                              <button type="button" className="action-reset-btn" onClick={() => setResetModal({ open: true, usuario })} title="Resetear contraseña">
                                <i className="fas fa-key" />
                              </button>
                              <button
                                type="button"
                                className={`action-toggle-btn ${usuario.activo ? 'danger' : 'success'}`}
                                onClick={() => setToggleModal({ open: true, usuario, nextActivo: !usuario.activo })}
                                disabled={actionLoading}
                                title={usuario.activo ? 'Desactivar' : 'Activar'}
                              >
                                <i className={`fas ${usuario.activo ? 'fa-ban' : 'fa-check-circle'}`} />
                              </button>
                              <button
                                type="button"
                                className="action-toggle-btn danger"
                                onClick={() => setQuitarModal({ open: true, usuario })}
                                disabled={actionLoading}
                                title="Quitar rol de administrador"
                              >
                                <i className="fas fa-user-minus" />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {editModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon"><i className="fas fa-user-edit" /></div>
            <h3>Editar administrador</h3>
            <p>Actualiza los datos de {getNombre(editModal.usuario)}.</p>
            <div className="modal-form">
              <div className="form-field">
                <label>Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm((prev) => ({ ...prev, email: e.target.value }))} />
              </div>
              <div className="form-field">
                <label>DNI</label>
                <input type="text" value={editForm.dni} onChange={(e) => setEditForm((prev) => ({ ...prev, dni: e.target.value }))} />
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Nombre</label>
                  <input type="text" value={editForm.nombre} onChange={(e) => setEditForm((prev) => ({ ...prev, nombre: e.target.value }))} />
                </div>
                <div className="form-field">
                  <label>Apellido</label>
                  <input type="text" value={editForm.apellido} onChange={(e) => setEditForm((prev) => ({ ...prev, apellido: e.target.value }))} />
                </div>
              </div>
              <div className="form-field">
                <label>Teléfono</label>
                <input type="text" value={editForm.telefono} onChange={(e) => setEditForm((prev) => ({ ...prev, telefono: e.target.value }))} />
              </div>
              {editError && (
                <div className="modal-message" style={{ borderColor: '#fecaca', background: '#fee2e2', color: '#7f1d1d' }}>
                  {editError}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setEditModal({ open: false, usuario: null })} disabled={actionLoading}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={handleSaveEdit} disabled={actionLoading}>
                {actionLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon"><i className={`fas ${toggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} /></div>
            <h3>{toggleModal.nextActivo ? 'Activar administrador' : 'Desactivar administrador'}</h3>
            <p>
              {toggleModal.nextActivo
                ? `${getNombre(toggleModal.usuario)} volverá a tener acceso a la cuenta.`
                : `${getNombre(toggleModal.usuario)} dejará de tener acceso a la cuenta.`}
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setToggleModal({ open: false, usuario: null, nextActivo: true })} disabled={actionLoading}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={handleToggleActivo} disabled={actionLoading}>
                {actionLoading ? 'Actualizando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {quitarModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon"><i className="fas fa-user-minus" /></div>
            <h3>Quitar rol de administrador</h3>
            <p>
              {getNombre(quitarModal.usuario)} dejará de administrar la cuenta. Seguirá como profesor y conservará sus cursos.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setQuitarModal({ open: false, usuario: null })} disabled={actionLoading}>Cancelar</button>
              <button type="button" className="btn-primary" onClick={handleQuitarRol} disabled={actionLoading}>
                {actionLoading ? 'Guardando...' : 'Quitar rol'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon"><i className="fas fa-key" /></div>
            <h3>Resetear contraseña</h3>
            <p>Esta acción reemplazará la contraseña actual de {getNombre(resetModal.usuario)}.</p>
            <div
              className="modal-message"
              style={{
                borderColor: getResetPassword(resetModal.usuario) ? '#bfdbfe' : '#fecaca',
                background: getResetPassword(resetModal.usuario) ? '#eff6ff' : '#fee2e2',
                color: getResetPassword(resetModal.usuario) ? '#1e3a8a' : '#7f1d1d'
              }}
            >
              Nuevo password: <strong>{getResetPassword(resetModal.usuario) || 'Sin DNI registrado'}</strong>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setResetModal({ open: false, usuario: null })} disabled={actionLoading}>Cancelar</button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleResetPassword}
                disabled={actionLoading || !getResetPassword(resetModal.usuario)}
              >
                {actionLoading ? 'Reseteando...' : 'Confirmar reset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resultModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${resultModal.ok ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
            </div>
            <h3>{resultModal.title}</h3>
            <p>{resultModal.message}</p>
            <div className="modal-actions">
              <button type="button" className="btn-primary" onClick={() => setResultModal({ open: false, ok: true, title: '', message: '' })}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProfesorParticularAdministradores;
